import React, { useRef, useState } from "react";
import { toast } from "sonner";
import {
  Upload, FileText, AlertTriangle, ShieldAlert, ListChecks,
  Loader2, Send, Printer, FileUp, X, GitCompareArrows,
} from "lucide-react";
import { useI18n } from "../lib/i18n";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";

const ACCEPTED_TYPES = ".pdf,.docx,.doc,.txt,.md,.png,.jpg,.jpeg,.webp,.heic,.bmp,.tiff";

const SEVERITY_STYLES = {
  high: "bg-red-50 text-red-800 border-red-200",
  medium: "bg-amber-50 text-amber-800 border-amber-200",
  low: "bg-emerald-50 text-emerald-800 border-emerald-200",
};

const PARTY_OPTIONS_AR = [
  { value: "", label: "— مراجعة محايدة (لا أمثّل طرفاً) —" },
  { value: "المؤجر", label: "المؤجر" },
  { value: "المستأجر", label: "المستأجر" },
  { value: "صاحب العمل", label: "صاحب العمل" },
  { value: "الموظف", label: "الموظف" },
  { value: "البائع", label: "البائع" },
  { value: "المشتري", label: "المشتري" },
  { value: "المُقرِض / الدائن", label: "المُقرِض / الدائن" },
  { value: "المقترِض / المدين", label: "المقترِض / المدين" },
  { value: "المقاول", label: "المقاول" },
  { value: "صاحب المشروع", label: "صاحب المشروع" },
  { value: "الطرف الأول", label: "الطرف الأول" },
  { value: "الطرف الثاني", label: "الطرف الثاني" },
  { value: "__custom__", label: "أخرى (أدخل يدوياً)…" },
];

const PARTY_OPTIONS_EN = [
  { value: "", label: "— Neutral review (no specific party) —" },
  { value: "Landlord", label: "Landlord" },
  { value: "Tenant", label: "Tenant" },
  { value: "Employer", label: "Employer" },
  { value: "Employee", label: "Employee" },
  { value: "Seller", label: "Seller" },
  { value: "Buyer", label: "Buyer" },
  { value: "Lender / Creditor", label: "Lender / Creditor" },
  { value: "Borrower / Debtor", label: "Borrower / Debtor" },
  { value: "Contractor", label: "Contractor" },
  { value: "Project Owner", label: "Project Owner" },
  { value: "First Party", label: "First Party" },
  { value: "Second Party", label: "Second Party" },
  { value: "__custom__", label: "Other (type manually)…" },
];

const severityLabel = (s, isAr) => {
  if (s === "high") return isAr ? "مرتفع" : "High";
  if (s === "medium") return isAr ? "متوسط" : "Medium";
  if (s === "low") return isAr ? "منخفض" : "Low";
  return s || "";
};

export default function DocumentReview() {
  const { lang } = useI18n();
  const isAr = lang === "ar";

  const [mode, setMode] = useState("upload"); // 'upload' | 'paste'
  const [file, setFile] = useState(null);
  const [pasted, setPasted] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [review, setReview] = useState(null);
  const [reviewId, setReviewId] = useState(null);
  const [partySelect, setPartySelect] = useState("");
  const [partyCustom, setPartyCustom] = useState("");
  const [suggestAmend, setSuggestAmend] = useState(false);
  const fileRef = useRef(null);

  const partyOptions = isAr ? PARTY_OPTIONS_AR : PARTY_OPTIONS_EN;
  const effectiveParty =
    partySelect === "__custom__" ? partyCustom.trim() : partySelect;

  const t = {
    eyebrow: isAr ? "مراجعة المستندات بالذكاء الاصطناعي" : "AI Document Review",
    title: isAr
      ? "ارفع مستندك، نُحلّله بدقّة محامٍ كويتي خبير."
      : "Upload your document — we analyse it like a senior Kuwaiti lawyer.",
    lead: isAr
      ? "ارفع عقداً أو إنذاراً أو حكماً، أو الصق نصّه مباشرة. يُعطيك سامي ملخّصاً مبسّطاً، يستخرج المخاطر، ويُشير إلى البنود الخطرة، ويقترح تعديلات أوّلية. التحليل مجاني وفوري."
      : "Upload a contract, formal notice, or judgment — or paste the text. Sami returns a plain-language summary, extracts risks, flags dangerous clauses, and suggests preliminary edits. Free and instant.",
    tabUpload: isAr ? "رفع ملف" : "Upload file",
    tabPaste: isAr ? "لصق نص" : "Paste text",
    dropFile: isAr
      ? "اضغط هنا أو اسحب الملف (PDF · DOCX · صورة · نص)"
      : "Click here or drop a file (PDF · DOCX · image · text)",
    fileLimit: isAr ? "حتى 25 م.ب." : "Up to 25 MB",
    pastePh: isAr ? "الصق نصّ المستند هنا..." : "Paste the document text here…",
    emailOpt: isAr ? "البريد الإلكتروني (اختياري — لإرسال نسخة)" : "Email (optional — to receive a copy)",
    emailPh: isAr ? "name@example.com" : "name@example.com",
    submit: isAr ? "حلّل المستند" : "Analyse document",
    analysing: isAr ? "جاري التحليل..." : "Analysing…",
    needContent: isAr ? "يُرجى رفع ملف أو لصق نص أولاً." : "Please upload a file or paste text first.",
    failed: isAr ? "فشل التحليل. حاول مرة أخرى." : "Analysis failed. Please try again.",
    success: isAr ? "تمّ التحليل" : "Analysis complete",
    emailedYes: isAr ? "وأُرسلت نسخة إلى بريدك." : "A copy was sent to your email.",
    documentType: isAr ? "نوع المستند" : "Document type",
    summaryTitle: isAr ? "الملخّص" : "Summary",
    risksTitle: isAr ? "المخاطر القانونية" : "Legal Risks",
    dangerTitle: isAr ? "البنود الخطيرة" : "Dangerous Clauses",
    suggTitle: isAr ? "اقتراحات تعديل" : "Suggested Edits",
    severity: isAr ? "الخطورة" : "Severity",
    print: isAr ? "حفظ / طباعة PDF" : "Save / Print PDF",
    newReview: isAr ? "مراجعة جديدة" : "New review",
    disclaimer: isAr
      ? "هذا تحليل أوّلي مولّد بالذكاء الاصطناعي ولا يُغني عن استشارة محامٍ مرخّص."
      : "AI-generated preliminary analysis. Not a substitute for licensed counsel.",
    why: isAr ? "السبب" : "Why",
    partyLabel: isAr ? "ما هو الطرف الذي تمثّله في هذا المستند؟" : "Which party do you represent?",
    partyHelp: isAr
      ? "اختياري — يجعل التحليل من منظور هذا الطرف ولمصلحته."
      : "Optional — tunes the review to favour the selected party.",
    partyCustomPh: isAr ? "اكتب صفة الطرف (مثلاً: المستفيد، الوكيل…)" : "Type the party (e.g. Beneficiary, Agent…)",
    amendLabel: isAr ? "اقتراح تعديلات نصية لصالح موكلي" : "Suggest textual amendments in my favour",
    amendHelp: isAr
      ? "يولّد لكل بند مشكل: «النص الأصلي ⬅ الصياغة المقترحة ⬅ السبب»."
      : "For each problematic clause: «original text → proposed wording → reason».",
    perspective: isAr ? "زاوية المراجعة" : "Review perspective",
    neutralReview: isAr ? "مراجعة محايدة" : "Neutral review",
    amendTitle: isAr ? "تعديلات نصية مقترحة (Redline)" : "Proposed Textual Amendments (Redline)",
    original: isAr ? "النص الأصلي" : "Original",
    proposed: isAr ? "الصياغة المقترحة" : "Proposed",
    clauseRef: isAr ? "المرجع" : "Reference",
  };

  const handleFile = (f) => {
    if (!f) return;
    if (f.size > 25 * 1024 * 1024) {
      toast.error(isAr ? "الملف أكبر من 25 م.ب." : "File too large (max 25 MB)");
      return;
    }
    setFile(f);
  };

  const onDrop = (e) => {
    e.preventDefault();
    handleFile(e.dataTransfer.files?.[0]);
  };

  const submit = async () => {
    if (mode === "upload" && !file) return toast.error(t.needContent);
    if (mode === "paste" && (!pasted || pasted.trim().length < 20))
      return toast.error(t.needContent);
    setBusy(true);
    setReview(null);
    try {
      const fd = new FormData();
      if (mode === "upload") fd.append("file", file);
      else fd.append("text", pasted);
      fd.append("language", lang);
      if (email) fd.append("email", email);
      if (effectiveParty) fd.append("party_role", effectiveParty);
      if (suggestAmend) fd.append("suggest_amendments", "true");
      const r = await api.post("/documents/review", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180_000,
      });
      setReview(r.data.review);
      setReviewId(r.data.id);
      toast.success(
        t.success + (r.data.email_sent ? " — " + t.emailedYes : ""),
      );
    } catch (err) {
      const msg = err?.response?.data?.detail || t.failed;
      toast.error(typeof msg === "string" ? msg : t.failed);
    } finally {
      setBusy(false);
    }
  };

  const printReport = () => {
    if (!review) return;
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "-10000px";
    document.body.appendChild(iframe);
    const doc = iframe.contentDocument;
    const sections = [];
    if (review.summary)
      sections.push({ h: t.summaryTitle, html: `<p>${review.summary}</p>` });
    if (review.risks?.length) {
      sections.push({
        h: t.risksTitle,
        html:
          "<ul>" +
          review.risks
            .map(
              (r) =>
                `<li><strong>${r.title || ""}</strong> <span style="color:#92400e">[${severityLabel(r.severity, isAr)}]</span><br>${r.description || ""}</li>`,
            )
            .join("") +
          "</ul>",
      });
    }
    if (review.dangerous_clauses?.length) {
      sections.push({
        h: t.dangerTitle,
        html:
          "<ul>" +
          review.dangerous_clauses
            .map(
              (d) =>
                `<li><em>«${d.clause || ""}»</em><br>${d.why || ""}</li>`,
            )
            .join("") +
          "</ul>",
      });
    }
    if (review.suggestions?.length) {
      sections.push({
        h: t.suggTitle,
        html:
          "<ul>" +
          review.suggestions
            .map((s) => `<li><strong>${s.title || ""}</strong><br>${s.detail || ""}</li>`)
            .join("") +
          "</ul>",
      });
    }
    if (review.amendments?.length) {
      const rows = review.amendments
        .map(
          (a) =>
            `<tr>
              <td style="padding:8px;border:1px solid #cbd5e1;background:#fef2f2;vertical-align:top;width:42%"><div style="font-size:10px;color:#9a3412;text-transform:uppercase;letter-spacing:.1em;margin-bottom:4px">${t.original}</div><div style="white-space:pre-wrap;line-height:1.7;color:#7f1d1d">${a.original_text || ""}</div></td>
              <td style="padding:8px;border:1px solid #cbd5e1;background:#ecfdf5;vertical-align:top;width:42%"><div style="font-size:10px;color:#065f46;text-transform:uppercase;letter-spacing:.1em;margin-bottom:4px">${t.proposed}</div><div style="white-space:pre-wrap;line-height:1.7;color:#064e3b">${a.suggested_text || ""}</div></td>
              <td style="padding:8px;border:1px solid #cbd5e1;vertical-align:top;color:#475569;font-size:12px"><div style="font-size:10px;color:#64748b;text-transform:uppercase;letter-spacing:.1em;margin-bottom:4px">${a.clause_ref || ""}</div>${a.why || ""}</td>
            </tr>`,
        )
        .join("");
      sections.push({
        h: t.amendTitle,
        html: `<table style="border-collapse:collapse;width:100%;font-size:12px">${rows}</table>`,
      });
    }
    const body = sections
      .map(
        (s) =>
          `<h2 style="margin-top:24px;border-bottom:1px solid #cbd5e1;padding-bottom:6px;font-family:Georgia,serif">${s.h}</h2>${s.html}`,
      )
      .join("");
    doc.open();
    doc.write(`<!DOCTYPE html><html lang="${lang}" dir="${isAr ? "rtl" : "ltr"}">
<head><meta charset="utf-8"><title>${t.eyebrow}</title>
<style>
@page { size: A4; margin: 18mm; }
body { font-family: 'Times New Roman', Times, serif; color:#1c1917; line-height:1.7; }
header { border-bottom: 2px solid #1c1917; padding-bottom: 12px; margin-bottom: 18px; }
header h1 { margin:0; font-family:Georgia,serif; font-size: 22px; }
header p { margin:4px 0 0; color:#78716c; font-size:12px; letter-spacing:0.12em; text-transform:uppercase; }
h2 { font-size: 16px; }
ul, ol { padding-${isAr ? "right" : "left"}: 20px; }
li { margin-bottom: 8px; }
footer { margin-top: 32px; border-top: 1px solid #cbd5e1; padding-top: 10px; color:#78716c; font-size:12px; }
</style></head>
<body>
<header>
  <h1>${isAr ? "مجموعة الدخيل القانونية" : "Aldakheel Legal Group"}</h1>
  <p>${t.eyebrow}</p>
</header>
<div><strong>${t.documentType}:</strong> ${review.document_type || "—"}</div>
${body}
<footer>${review.disclaimer || t.disclaimer}</footer>
</body></html>`);
    doc.close();
    iframe.contentWindow.focus();
    setTimeout(() => {
      iframe.contentWindow.print();
      setTimeout(() => document.body.removeChild(iframe), 1500);
    }, 250);
  };

  const reset = () => {
    setReview(null);
    setReviewId(null);
    setFile(null);
    setPasted("");
    setPartySelect("");
    setPartyCustom("");
    setSuggestAmend(false);
  };

  return (
    <div data-testid="document-review-page" className="bg-white">
      <section className="border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 pt-16 pb-12">
          <p className="eyebrow">{t.eyebrow}</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.05] tracking-tight">
            {t.title}
          </h1>
          <p className="mt-6 max-w-3xl text-lg text-slate-600 leading-relaxed">{t.lead}</p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 lg:px-10 py-12">
        {!review && (
          <div className="bg-slate-50 border border-slate-200 p-6 lg:p-10">
            <div className="flex gap-2 mb-6">
              <button
                type="button"
                onClick={() => setMode("upload")}
                data-testid="docrev-tab-upload"
                className={`px-4 py-2 text-sm border ${mode === "upload" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300"}`}
              >
                <Upload className="w-4 h-4 inline-block me-2" />
                {t.tabUpload}
              </button>
              <button
                type="button"
                onClick={() => setMode("paste")}
                data-testid="docrev-tab-paste"
                className={`px-4 py-2 text-sm border ${mode === "paste" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300"}`}
              >
                <FileText className="w-4 h-4 inline-block me-2" />
                {t.tabPaste}
              </button>
            </div>

            {mode === "upload" ? (
              <div>
                <div
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={onDrop}
                  onClick={() => fileRef.current?.click()}
                  data-testid="docrev-dropzone"
                  className="border-2 border-dashed border-slate-300 hover:border-slate-900 transition-colors p-10 text-center cursor-pointer bg-white"
                >
                  <FileUp className="w-10 h-10 mx-auto text-slate-400 mb-3" />
                  <p className="text-slate-700 font-medium">{t.dropFile}</p>
                  <p className="text-slate-500 text-sm mt-1">{t.fileLimit}</p>
                  <input
                    ref={fileRef}
                    type="file"
                    accept={ACCEPTED_TYPES}
                    className="hidden"
                    data-testid="docrev-file-input"
                    onChange={(e) => handleFile(e.target.files?.[0])}
                  />
                </div>
                {file && (
                  <div className="mt-4 flex items-center justify-between bg-white border border-slate-200 p-3" data-testid="docrev-file-chip">
                    <div className="flex items-center gap-2 text-sm text-slate-700">
                      <FileText className="w-4 h-4 text-slate-500" />
                      <span className="font-medium">{file.name}</span>
                      <span className="text-slate-500">
                        ({(file.size / 1024).toFixed(1)} KB)
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setFile(null)}
                      data-testid="docrev-file-remove"
                      className="text-slate-400 hover:text-slate-900"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Textarea
                value={pasted}
                onChange={(e) => setPasted(e.target.value)}
                placeholder={t.pastePh}
                rows={10}
                data-testid="docrev-paste-input"
                className="rounded-sm bg-white"
              />
            )}

            <div className="mt-6 grid gap-5 lg:grid-cols-2">
              <div data-testid="docrev-party-block">
                <Label className="text-xs uppercase tracking-wider text-slate-500">
                  {t.partyLabel}
                </Label>
                <select
                  value={partySelect}
                  onChange={(e) => {
                    setPartySelect(e.target.value);
                    if (e.target.value !== "__custom__") setPartyCustom("");
                  }}
                  data-testid="docrev-party-select"
                  className="mt-2 w-full h-10 px-3 bg-white border border-slate-300 rounded-sm text-sm text-slate-900 focus:outline-none focus:border-slate-900"
                >
                  {partyOptions.map((o) => (
                    <option key={o.value || "neutral"} value={o.value}>{o.label}</option>
                  ))}
                </select>
                {partySelect === "__custom__" && (
                  <Input
                    value={partyCustom}
                    onChange={(e) => setPartyCustom(e.target.value)}
                    placeholder={t.partyCustomPh}
                    data-testid="docrev-party-custom-input"
                    className="rounded-sm bg-white mt-2"
                    maxLength={120}
                  />
                )}
                <p className="text-xs text-slate-500 mt-2 leading-relaxed">{t.partyHelp}</p>
              </div>

              <div data-testid="docrev-amend-block">
                <Label className="text-xs uppercase tracking-wider text-slate-500">
                  {isAr ? "الاقتراحات التعديلية" : "Amendment suggestions"}
                </Label>
                <label
                  className="mt-2 flex items-start gap-3 bg-white border border-slate-300 px-3 py-3 cursor-pointer hover:border-slate-900 transition-colors"
                  htmlFor="docrev-amend-toggle"
                >
                  <input
                    id="docrev-amend-toggle"
                    type="checkbox"
                    checked={suggestAmend}
                    onChange={(e) => setSuggestAmend(e.target.checked)}
                    data-testid="docrev-amend-toggle"
                    className="mt-1 w-4 h-4 accent-slate-900"
                  />
                  <span className="flex-1">
                    <span className="block text-sm font-medium text-slate-900">{t.amendLabel}</span>
                    <span className="block text-xs text-slate-500 mt-1 leading-relaxed">{t.amendHelp}</span>
                  </span>
                  <GitCompareArrows className="w-4 h-4 text-slate-400 mt-1" />
                </label>
              </div>
            </div>

            <div className="mt-6">
              <Label htmlFor="docrev-email" className="text-xs uppercase tracking-wider text-slate-500">
                {t.emailOpt}
              </Label>
              <Input
                id="docrev-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t.emailPh}
                data-testid="docrev-email-input"
                className="rounded-sm bg-white mt-2"
                type="email"
              />
            </div>

            <div
              className="mt-6 border border-amber-300 bg-amber-50 p-4 rounded-sm text-xs text-amber-900 leading-relaxed"
              data-testid="docrev-disclaimer"
            >
              {isAr ? (
                <>
                  <b>إقرار قبل رفع المستند:</b> أقر بأنني أملك الحق القانوني في رفع ومشاركة هذا المستند، وأن أي بيانات أو معلومات أو مستندات أقدمها عبر الموقع أو خدمات الذكاء الاصطناعي يتم تقديمها على مسؤوليتي الشخصية، وأفهم أن التحليل الناتج هو تحليل أولي لا يغني عن المراجعة القانونية المتخصصة.{" "}
                  <a href="/terms" target="_blank" className="font-medium underline underline-offset-2">الشروط والأحكام</a>
                </>
              ) : (
                <>
                  <b>Document upload notice:</b> I confirm I have the legal right to upload and share this document, that any data I submit via the site or AI services is provided at my own responsibility, and that the resulting analysis is preliminary and does not replace specialised legal review.{" "}
                  <a href="/terms" target="_blank" className="font-medium underline underline-offset-2">Terms</a>
                </>
              )}
            </div>

            <Button
              onClick={submit}
              disabled={busy}
              data-testid="docrev-submit-btn"
              className="mt-4 rounded-sm h-12 px-8 bg-slate-900 hover:bg-slate-800 text-white"
            >
              {busy ? (
                <>
                  <Loader2 className="w-4 h-4 me-2 animate-spin" />
                  {t.analysing}
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 me-2" />
                  {t.submit}
                </>
              )}
            </Button>
            {busy && (
              <p className="mt-4 text-sm text-slate-500" data-testid="docrev-busy-hint">
                {isAr
                  ? "قد يستغرق التحليل من 30 إلى 90 ثانية حسب طول المستند."
                  : "Analysis usually takes 30 to 90 seconds depending on length."}
              </p>
            )}
          </div>
        )}

        {review && (
          <div data-testid="docrev-result">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
              <div>
                <p className="eyebrow">{t.documentType}</p>
                <h2 className="font-display text-3xl mt-2" data-testid="docrev-doc-type">
                  {review.document_type || "—"}
                </h2>
                {review.party_perspective && (
                  <p className="mt-3 text-sm text-slate-600" data-testid="docrev-perspective">
                    <span className="text-xs uppercase tracking-wider text-slate-400 me-2">
                      {t.perspective}:
                    </span>
                    <span className="inline-block px-3 py-1 bg-slate-900 text-white text-xs">
                      {review.party_perspective}
                    </span>
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={printReport}
                  variant="outline"
                  className="rounded-sm"
                  data-testid="docrev-print-btn"
                >
                  <Printer className="w-4 h-4 me-2" />
                  {t.print}
                </Button>
                <Button
                  onClick={reset}
                  variant="outline"
                  className="rounded-sm"
                  data-testid="docrev-reset-btn"
                >
                  {t.newReview}
                </Button>
              </div>
            </div>

            {review.summary && (
              <div className="bg-slate-50 border border-slate-200 p-6 mb-6">
                <h3 className="eyebrow flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  {t.summaryTitle}
                </h3>
                <p className="mt-3 text-slate-700 leading-relaxed whitespace-pre-wrap" data-testid="docrev-summary">
                  {review.summary}
                </p>
              </div>
            )}

            {review.risks?.length > 0 && (
              <div className="mb-6" data-testid="docrev-risks">
                <h3 className="eyebrow flex items-center gap-2 mb-4">
                  <AlertTriangle className="w-4 h-4 text-amber-600" />
                  {t.risksTitle}
                </h3>
                <div className="grid gap-3">
                  {review.risks.map((r, i) => (
                    <div
                      key={`risk-${i}-${(r.title || "").slice(0, 24)}`}
                      className={`border p-4 ${SEVERITY_STYLES[r.severity] || "bg-slate-50 border-slate-200"}`}
                      data-testid={`docrev-risk-${i}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <h4 className="font-medium">{r.title}</h4>
                        <span className="text-xs uppercase tracking-wider px-2 py-0.5 bg-white/60 border border-current">
                          {severityLabel(r.severity, isAr)}
                        </span>
                      </div>
                      <p className="text-sm mt-2 leading-relaxed">{r.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {review.dangerous_clauses?.length > 0 && (
              <div className="mb-6" data-testid="docrev-clauses">
                <h3 className="eyebrow flex items-center gap-2 mb-4">
                  <ShieldAlert className="w-4 h-4 text-red-600" />
                  {t.dangerTitle}
                </h3>
                <div className="grid gap-3">
                  {review.dangerous_clauses.map((d, i) => (
                    <div
                      key={`clause-${i}-${(d.clause || "").slice(0, 24)}`}
                      className="border border-red-200 bg-red-50 p-4"
                      data-testid={`docrev-clause-${i}`}
                    >
                      <blockquote className="text-red-900 font-medium italic border-s-2 border-red-400 ps-3">
                        «{d.clause}»
                      </blockquote>
                      <p className="text-sm text-red-800 mt-3 leading-relaxed">
                        <strong className="me-1">{t.why}:</strong> {d.why}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {review.suggestions?.length > 0 && (
              <div className="mb-6" data-testid="docrev-suggestions">
                <h3 className="eyebrow flex items-center gap-2 mb-4">
                  <ListChecks className="w-4 h-4 text-emerald-600" />
                  {t.suggTitle}
                </h3>
                <div className="grid gap-3">
                  {review.suggestions.map((s, i) => (
                    <div
                      key={`suggestion-${i}-${(s.title || "").slice(0, 24)}`}
                      className="border border-slate-200 bg-white p-4"
                      data-testid={`docrev-suggestion-${i}`}
                    >
                      <h4 className="font-medium text-slate-900">{s.title}</h4>
                      <p className="text-sm text-slate-700 mt-2 leading-relaxed">{s.detail}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {review.amendments?.length > 0 && (
              <div className="mb-6" data-testid="docrev-amendments">
                <h3 className="eyebrow flex items-center gap-2 mb-4">
                  <GitCompareArrows className="w-4 h-4 text-indigo-600" />
                  {t.amendTitle}
                </h3>
                <div className="grid gap-4">
                  {review.amendments.map((a, i) => (
                    <div
                      key={`amend-${i}-${(a.clause_ref || a.original_text || "").slice(0, 24)}`}
                      className="border border-slate-200 bg-white"
                      data-testid={`docrev-amendment-${i}`}
                    >
                      {a.clause_ref && (
                        <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
                          {t.clauseRef}: <span className="text-slate-800 normal-case font-medium">{a.clause_ref}</span>
                        </div>
                      )}
                      <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-slate-200">
                        <div className="p-4 bg-red-50">
                          <div className="text-[10px] uppercase tracking-wider text-red-700 mb-2">
                            {t.original}
                          </div>
                          <p className="text-sm text-red-900 leading-relaxed whitespace-pre-wrap">
                            {a.original_text}
                          </p>
                        </div>
                        <div className="p-4 bg-emerald-50">
                          <div className="text-[10px] uppercase tracking-wider text-emerald-700 mb-2">
                            {t.proposed}
                          </div>
                          <p className="text-sm text-emerald-900 leading-relaxed whitespace-pre-wrap">
                            {a.suggested_text}
                          </p>
                        </div>
                      </div>
                      {a.why && (
                        <div className="px-4 py-3 bg-white border-t border-slate-200">
                          <span className="text-xs uppercase tracking-wider text-slate-500 me-2">
                            {t.why}:
                          </span>
                          <span className="text-sm text-slate-700">{a.why}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <p className="text-xs text-slate-500 mt-8 leading-relaxed" data-testid="docrev-disclaimer">
              {review.disclaimer || t.disclaimer}
              {reviewId ? ` · ID: ${reviewId.slice(0, 8)}` : ""}
            </p>
          </div>
        )}
      </section>
    </div>
  );
}
